import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { mcrService } from '../../../../services/mcrService';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  useTheme,
  IconButton,
  Divider,
  Stack
} from '@mui/material';
import {
  Settings,
  CheckCircle,
  Error as ErrorIcon,
  VpnKey,
  Warning,
  Lock,
  Save,
  Info,
  QrCode,
  ContentCopy,
  Download,
  Print,
  Lightbulb
} from '@mui/icons-material';

const MCRSettings = ({ user, onCodeUpdate }) => {
  const theme = useTheme();
  const [currentCode, setCurrentCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isCodeUser = user?.accessCode !== undefined;
  
  // Get the MCR access URL - use production URL
  const mcrAccessURL = 'http://mtoconcepcion.online/#/portal/civil-registry/orders';

  const handleDownloadQR = () => {
    const svg = document.getElementById('mcr-qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = 'MCR-Access-QRCode.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleCopyURL = () => {
    navigator.clipboard.writeText(mcrAccessURL);
    setSuccess('Access URL copied to clipboard!');
  };

  const handleUpdateCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!currentCode.trim() || !newCode.trim() || !confirmCode.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (newCode !== confirmCode) {
      setError('New code and confirmation do not match');
      return;
    }

    if (newCode.length < 6) {
      setError('New code must be at least 6 characters long');
      return;
    }

    if (currentCode === newCode) {
      setError('New code must be different from current code');
      return;
    }

    setLoading(true);

    try {
      // Verify current code
      const verifyResult = await mcrService.verifyAccessCode(currentCode);
      
      if (!verifyResult.success) {
        setError('Current access code is incorrect');
        setLoading(false);
        return;
      }

      // Update to new code
      const updateResult = await mcrService.updateAccessCode(currentCode, newCode);

      if (updateResult.success) {
        setSuccess('Access code updated successfully! Please use your new code next time.');
        
        // Update session storage with new code
        sessionStorage.setItem('mcr_access', JSON.stringify({
          code: newCode,
          accessType: 'code',
          timestamp: new Date().toISOString()
        }));

        // Clear form
        setCurrentCode('');
        setNewCode('');
        setConfirmCode('');

        // Notify parent if callback provided
        if (onCodeUpdate) {
          onCodeUpdate(newCode);
        }
      } else {
        setError(updateResult.error || 'Failed to update access code');
      }
    } catch (err) {
      setError('An error occurred while updating the code');
    }

    setLoading(false);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <style>{`
        @media print {
          @page {
            size: letter portrait;
            margin: 1in;
          }
          body * {
            visibility: hidden;
          }
          .print-qr-section, .print-qr-section * {
            visibility: visible;
          }
          .print-qr-section {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
        }
      `}</style>

      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          className="no-print"
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Settings sx={{ mr: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              MCR Settings
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Manage your access code and preferences
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Update Access Code Section - Only for Code Users */}
          {isCodeUser && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2 }} className="no-print">
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}>
                  <VpnKey sx={{ mr: 1, color: 'primary.main' }} />
                  Update Access Code
                </Typography>

                <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Important Security Notes:</Typography>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    <li>Choose a strong, unique code (minimum 6 characters)</li>
                    <li>Mix letters and numbers for better security</li>
                    <li>Do not share your code with anyone</li>
                    <li>You'll need to re-enter this code when accessing the system</li>
                  </ul>
                </Alert>

                <form onSubmit={handleUpdateCode}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="password"
                        label="Current Access Code"
                        value={currentCode}
                        onChange={(e) => setCurrentCode(e.target.value.toUpperCase())}
                        required
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="New Access Code"
                        value={newCode}
                        onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                        required
                        inputProps={{ minLength: 6 }}
                        helperText="Code will be automatically converted to uppercase"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><VpnKey /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Confirm New Code"
                        value={confirmCode}
                        onChange={(e) => setConfirmCode(e.target.value.toUpperCase())}
                        required
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><CheckCircle /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        sx={{ minWidth: 200 }}
                      >
                        {loading ? 'Updating...' : 'Update Access Code'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          )}

          {/* Current Session Info - Only for Code Users */}
          {isCodeUser && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }} className="no-print">
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Info sx={{ mr: 1, color: 'text.secondary' }} />
                  Session Information
                </Typography>
                <Box sx={{ color: 'text.secondary' }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Access Type:</strong> Registry Staff (Code-Based)
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Current Code:</strong> {user?.accessCode ? '••••••••' : 'Hidden'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Session:</strong> Active (clears on browser close)
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* QR Code Section - Available for All Users */}
          <Grid item xs={12}>
            <Paper className="print-qr-section" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.primary' }}>
                <QrCode sx={{ mr: 1, color: 'primary.main' }} />
                MCR System Access QR Code
              </Typography>

              <Alert severity="info" sx={{ mb: 4, maxWidth: 600, mx: 'auto', textAlign: 'left' }} className="no-print">
                Share this QR code to allow staff members to quickly access the MCR System.
                They will still need to enter their access code after scanning.
              </Alert>

              <Box sx={{ mb: 4 }}>
                <Box sx={{
                  display: 'inline-block',
                  p: 3,
                  border: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: 2,
                  bgcolor: 'white'
                }}>
                  <QRCodeSVG
                    id="mcr-qr-code"
                    value={mcrAccessURL}
                    size={200}
                    level="H"
                    includeMargin={true}
                    fgColor="#1f2937"
                    bgColor="#ffffff"
                  />
                </Box>
              </Box>

              <Box className="no-print" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1} align="left">
                  Access URL:
                </Typography>
                <TextField
                  fullWidth
                  value={mcrAccessURL}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopyURL} edge="end" color="primary">
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: 'grey.50' }}
                />
              </Box>

              <Stack direction="row" spacing={2} justifyContent="center" className="no-print">
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleDownloadQR}
                >
                  Download QR
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={() => window.print()}
                >
                  Print QR
                </Button>
              </Stack>

              <Box sx={{ display: 'none' }} className="print-only">
                <Typography variant="body1" align="center" sx={{ mt: 2, fontWeight: 'bold' }}>
                  {mcrAccessURL}
                </Typography>
                <Box sx={{ mt: 4, border: '1px solid black', p: 2, borderRadius: 1, textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>How to use:</Typography>
                  <ol>
                    <li>Scan the QR code with a smartphone camera</li>
                    <li>Browser will open the MCR System access page</li>
                    <li>Enter your access code to login</li>
                    <li>Start using the system immediately</li>
                  </ol>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Feedback Snackbar */}
        <Snackbar
          open={Boolean(error || success)}
          autoHideDuration={3000}
          onClose={() => { setError(''); setSuccess(''); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => { setError(''); setSuccess(''); }}
            severity={error ? 'error' : 'success'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error || success}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MCRSettings;
